import Drawer from 'rc-drawer';
import { toast } from 'react-toastify';
import { IListItem } from '@nkr/models/list-item';
import { ICoachFilteredListItem } from '@nkr/store/microsites/hooks/use-coach-filtered-list';
import useCoachDrawerDetail, { IChangeCoachStatusRequest } from '@nkr/store/microsites/hooks/use-coach-drawer-detail';
import { Button, Dropdown, Glyphicon, MenuItem, Panel } from 'react-bootstrap';
import { ViewWrapper, drawerStyles } from './styles';
import DetailHeader from './detail-header';
import CoachStatusForm from './coach-status-form';
import AuditLog from './log/AuditLog';
import BusinessCardsRequest from './business-cards-request';
import ConfirmAction from './confirm-action';
import { ConfirmActions } from './confirm-actions';

interface IProps {
    id: number | null;
    coachStatusList: IListItem[];
    isAdmin: boolean;
    currentTab: string;
    onClose: () => void;
    onItemUpdated: (patch: Partial<ICoachFilteredListItem>) => void;
}

export default function CoachDrawer({ id, coachStatusList, isAdmin, currentTab, onClose, onItemUpdated }: IProps) {
    const { data, loading, error, saveCoachStatus, saveLogEntry, deleteLogEntry, sendInvitationEmail, sendDashboardLinkEmail } = useCoachDrawerDetail(id);
    
    const handleGotoProfile = () => {
        window.open(`/microsite/coach/${id}?previous=${currentTab}`, '_blank');
    };

    const handleEditMicrosite = () => {
        if (!data) {
            return;
        }
        window.open(`${__NKR_ORG_URL__}/microsite/custom-site/${id}/${data.editKey}`, '_blank');
    };

    const handleEditRegistration = () => {
        window.location.href = `/microsite/coach/registration/${id}?returnTo=${window.location.pathname.slice(1)}`;
    };

    const handleViewActivityLog = () => {
        window.location.href = `/microsite/coach/history/${id}?returnTo=${window.location.pathname.slice(1)}`;
    };

    const handleResendInvitation = async () => {
        await sendInvitationEmail();
        toast.success('Invitation email sent successfully.');
    };

    const handleSendDashboardLink = async () => {
        await sendDashboardLinkEmail();
        toast.success('Dashboard link sent successfully.');
    };

    const handleEditDemographic = () => {
        if (!data) {
            return;
        }

        let hrefPrefix = isAdmin ? '/admin/microsite' : `/center/${data.center.id}/microsite`;

        window.location.href = `${hrefPrefix}/${id}/demographic?previous=${currentTab}`;
    };

    const handleCoachStatusChange = async (_recipient: any, request: IChangeCoachStatusRequest) => {
        await saveCoachStatus(request);
        toast.success('Coach status updated successfully.');
        const statusItem = coachStatusList.find(s => s.id == request.newCoachStatus);
        // Append T00:00:00 so the browser parses as local midnight, not UTC midnight.
        // Without it, YYYY-MM-DD strings display with time offset in the grid.
        const toLocal = (val: string | null) => val ? `${val}T00:00:00` : null;
        onItemUpdated({
            coachStatusDescription: statusItem?.description || null,
            nextFollowupDate: toLocal(request.nextFollowupDate),
            mostRecentCoachContactDateTime: toLocal(request.mostRecentContactDate),
        });
    };

    const handleSaveLogEntry = (entryId: number, _recipientId: number, content: string, actionType: number, isPinned: boolean) => {
        saveLogEntry(entryId, content, actionType, isPinned);
    };

    const handleDeleteLogEntry = (entryId: number, _recipientId: number) => {
        deleteLogEntry(entryId);
    };

    const handleBusinessCardUpdate = (req: any) => {
        if (req?.businessCardsRequestDate) {
            onItemUpdated({ businessCardsRequestDate: new Date(req.businessCardsRequestDate).toISOString() });
            toast.success('Business cards requested successfully.');
        }
    };

    // Patch data shape for sub-components that were built against IMicrositeRecipientForList.
    // coachContact.fullName is surfaced as flat coachFullName in the drawer detail.
    // mostRecentCoachNotificationEvent.eventDate is surfaced as mostRecentCoachNotificationEventDate.
    const headerData = data ? {
        ...data,
        coachContact: data.coachFullName ? { fullName: data.coachFullName } : null,
    } : null;

    const statusFormData = data ? {
        ...data,
        mostRecentCoachNotificationEvent: data.mostRecentCoachNotificationEventDate
            ? { eventDate: data.mostRecentCoachNotificationEventDate }
            : null,
    } : null;

    return (
        <>
            <Drawer
                width="32vw"
                placement="right"
                open={id !== null}
                className='microsite-view'
            >
                <div style={drawerStyles.container}>
                    <div style={drawerStyles.scrollBody}>
                        <div style={drawerStyles.titleRow}>
                            <h3 style={drawerStyles.title}>Microsite Registration Details</h3>
                            <Button style={drawerStyles.closeButton} onClick={onClose}><Glyphicon glyph="remove" /></Button>
                        </div>

                        {loading && <div>Loading...</div>}
                        {error && <div className='text-danger'>{error}</div>}

                        {data && (
                            <>
                                <DetailHeader data={headerData as any} champion={data.champion} isAdmin={isAdmin} />

                                <CoachStatusForm
                                    data={statusFormData as any}
                                    coachStatusList={coachStatusList}
                                    onCoachStatusChange={handleCoachStatusChange}
                                />

                                <ViewWrapper>
                                    <div className='modal-container'>
                                        <Panel>
                                            <Panel.Body>
                                                <Dropdown dropup={false} id='microsite-coach-drawer-actions' style={drawerStyles.fullWidth}>
                                                    <Dropdown.Toggle bsStyle='primary' style={drawerStyles.fullWidth}>
                                                        Actions
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu style={drawerStyles.fullWidth}>
                                                        <MenuItem onClick={handleGotoProfile}>Go to Patient's Profile Page</MenuItem>
                                                        <MenuItem onClick={handleEditMicrosite}>Edit Microsite</MenuItem>
                                                        <BusinessCardsRequest recipient={data as any} onUpdate={handleBusinessCardUpdate} />
                                                        <MenuItem onClick={handleViewActivityLog}>View Activity Log</MenuItem>
                                                        <MenuItem onClick={handleEditRegistration}>Edit Registration</MenuItem>
                                                        <MenuItem onClick={handleEditDemographic}>Edit Demographic</MenuItem>
                                                        <ConfirmAction
                                                            name={data.fullName}
                                                            action={ConfirmActions.ResendInvitationEmail}
                                                            onConfirm={handleResendInvitation}
                                                        />
                                                        <ConfirmAction
                                                            name={data.fullName}
                                                            action={ConfirmActions.SendDashboardLink}
                                                            onConfirm={handleSendDashboardLink}
                                                        />
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </Panel.Body>
                                        </Panel>

                                        <Panel>
                                            <Panel.Body>
                                                <AuditLog
                                                    data={data as any}
                                                    onSaveCoachLogEntry={handleSaveLogEntry}
                                                    onDeleteLogEntry={handleDeleteLogEntry}
                                                />
                                            </Panel.Body>
                                        </Panel>
                                    </div>
                                </ViewWrapper>
                            </>
                        )}
                    </div>

                    <div className='text-center bg-info'>
                        <Button bsStyle='link' block onClick={onClose}>Close This View</Button>
                    </div>
                </div>
            </Drawer>
        </>
    );
}
